import { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/shared/cn';
import { UPLOAD_FOLDER_ICON, UPLOAD_ICON } from '@/shared/upload-icons';
import { MENU_CONTENT, MENU_ITEM_BASE } from '@/shared/ui-class';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';
import { SharePointBrowseModal } from './SharePointBrowseModal';

export function PaperclipAttachButton() {
  const [open, setOpen] = useState(false);
  const [sharePointOpen, setSharePointOpen] = useState(false);
  const [pending, setPending] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useClickOutside(ref, () => setOpen(false), open);

  const attachNames = (names: string[]) => {
    setPending((prev) => [...prev, ...names]);
    setOpen(false);
  };

  const removePending = (name: string) => {
    setPending((prev) => prev.filter((item) => item !== name));
  };

  return (
    <>
      <div className="relative flex min-w-0 items-center gap-1" ref={ref}>
        {pending.map((name) => (
          <span
            key={name}
            className="inline-flex max-w-[8rem] items-center gap-1 truncate rounded-full bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800"
          >
            <span className="truncate">{name}</span>
            <button
              type="button"
              aria-label={`Remove ${name}`}
              onClick={() => removePending(name)}
              className="shrink-0 rounded-full text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <button
          type="button"
          aria-label="Upload files"
          title="Upload files"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-100 dark:focus-visible:ring-zinc-700',
            open && 'bg-zinc-100 dark:bg-zinc-800/60',
          )}
        >
          <UPLOAD_ICON className="h-4 w-4" />
        </button>
        {open ? (
          <div
            className={cn(
              MENU_CONTENT,
              'absolute bottom-full left-0 z-50 mb-2 min-w-[180px] p-1',
            )}
          >
            <button
              type="button"
              className={MENU_ITEM_BASE}
              onClick={() => fileInputRef.current?.click()}
            >
              <UPLOAD_ICON className="h-4 w-4 text-zinc-500" />
              Upload files
            </button>
            <button
              type="button"
              className={MENU_ITEM_BASE}
              onClick={() => attachNames(['uploads/mock-folder'])}
            >
              <UPLOAD_FOLDER_ICON className="h-4 w-4 text-zinc-500" />
              Upload folder
            </button>
            <button
              type="button"
              className={MENU_ITEM_BASE}
              onClick={() => {
                setOpen(false);
                setSharePointOpen(true);
              }}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded bg-[#0078d4] text-[10px] font-bold text-white">
                SP
              </span>
              SharePoint
            </button>
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(event) => {
            const names = Array.from(event.target.files ?? []).map((file) => file.name);
            event.target.value = '';
            if (names.length > 0) attachNames(names);
          }}
        />
      </div>
      <SharePointBrowseModal
        open={sharePointOpen}
        onOpenChange={setSharePointOpen}
        onAttach={(names) => {
          attachNames(names);
          setSharePointOpen(false);
        }}
      />
    </>
  );
}
